"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { useId, useRef, useState, type FormEvent } from "react";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full rounded-[10px] border border-border bg-surface-100 px-4 font-daylight-body text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-ash focus:border-primary focus:ring-2 focus:ring-primary/20 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-destructive/20";

const autocompleteByType = {
  email: "email",
  phone: "tel",
  "short-text": "name",
} as const;

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const clearError = (name: string) =>
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }

    const formData = new FormData(form);
    const valueFor = (fieldType: (typeof fields)[number]["fieldType"]) => {
      const index = fields.findIndex((field) => field.fieldType === fieldType);
      if (index < 0) return "";
      return String(formData.get(`field-${index}`) || "").trim();
    };
    const phone = valueFor("phone");
    const subject = valueFor("select");

    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      const client = getBaseApiClient();
      if (!client) throw new Error("contact-client-unavailable");
      await client.contact.submit({
        email: valueFor("email"),
        message: valueFor("long-text"),
        name: valueFor("short-text"),
        phone: phone || undefined,
        sourcePath: window.location.pathname,
        subject: subject || undefined,
      });
      setSucceeded(true);
      form.reset();
    } catch {
      setSubmissionFailed(true);
      window.requestAnimationFrame(() => failureRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="relative overflow-hidden rounded-[28px] border border-cobalt/20 bg-mint p-7 text-ink before:absolute before:right-7 before:top-7 before:size-12 before:rounded-full before:border before:border-cobalt/30 md:p-10"
        role="status"
      >
        <p className="mb-5 font-daylight-body text-sm font-semibold text-cobalt">
          Inquiry received
        </p>
        <DaylightRichText
          className="max-w-[65ch] text-ink [&_h3]:font-daylight-display [&_h3]:text-3xl [&_h3]:font-semibold"
          content={successMessage}
        />
      </div>
    );

  return (
    <form className="flex flex-col gap-6" noValidate onSubmit={submit} ref={formRef}>
      {submissionFailed ? (
        <div
          className="rounded-[20px] border border-destructive bg-melon p-5 text-ink outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2"
          ref={failureRef}
          role="alert"
          tabIndex={-1}
        >
          <p className="font-daylight-display text-xl font-semibold">Your inquiry was not sent.</p>
          <DaylightRichText className="mt-2 text-base text-ink" content={errorMessage} />
          <button
            className="mt-4 inline-flex min-h-11 items-center rounded-full border border-ink px-4 font-daylight-body text-sm font-semibold transition-colors hover:bg-ink hover:text-daylight focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            onClick={() => formRef.current?.requestSubmit()}
            type="button"
          >
            Try sending again
          </button>
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          onInput: () => clearError(name),
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-daylight-body text-sm font-semibold leading-[1.5]">
            {field.label}
            {field.required ? <span className="ml-1 text-slate">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="signal-checkbox mt-0.5 size-5 shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                <label htmlFor={inputId}>{label}</label>
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select
                    {...shared}
                    className={`${controlClass} signal-select`}
                    defaultValue={field.placeholder ? "" : undefined}
                  >
                    {field.placeholder ? (
                      <option disabled value="">
                        {field.placeholder}
                      </option>
                    ) : null}
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    autoComplete={
                      field.fieldType in autocompleteByType
                        ? autocompleteByType[field.fieldType as keyof typeof autocompleteByType]
                        : undefined
                    }
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[+()0-9 .-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-daylight-body text-sm font-medium text-destructive" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="group inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-[8px] border border-primary bg-primary px-6 font-daylight-mono text-[0.6875rem] font-medium uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:border-signal-dim hover:bg-signal-dim focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        <span>{submitting ? "Sending inquiry…" : submitLabel}</span>
        <span
          aria-hidden="true"
          className="text-base leading-none transition-transform duration-200 group-hover:translate-x-1"
        >
          →
        </span>
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
